import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { signInSchema, validate } from '../../utils/validators';
import { logger, generateRequestId } from '../../utils/logger';
import { trackSignIn } from '../../hooks/useGoogleAnalytics';


const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, resendVerificationEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [searchParams] = useSearchParams();
  const referralCode = searchParams?.get('ref');
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Check if user came from email verification
  useEffect(() => {
    if (location?.state?.emailVerified) {
      setSuccessMessage('Email verified successfully! You can now sign in.');
      if (location?.state?.email) {
        setEmail(location?.state?.email);
      }
      // Clear the state
      window.history?.replaceState({}, document.title);
    }
  }, [location]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const validateForm = () => {
    const validation = validate(signInSchema, { email, password });
    
    if (!validation?.success) {
      setError(validation?.errors);
      return false;
    }
    
    setError({});
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    
    // Input sanitization - remove any control characters
    const sanitizedValue = value?.replace(/[\r\n\0]/g, '');
    
    if (name === 'email') {
      setEmail(sanitizedValue);
    } else if (name === 'password') {
      setPassword(sanitizedValue);
    }
    
    // Clear error for this field when user starts typing
    if (error?.[name]) {
      setError(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (generalError) {
      setGeneralError('');
      setShowResendVerification(false);
      setResendSuccess(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setGeneralError('');
    setShowResendVerification(false);
    setResendSuccess(false);
    
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);
    
    try {
      const { data, error } = await signIn(email, password);
      
      if (error) {
        // Check for specific error types and provide user-friendly messages
        const errorMessage = error?.message?.toLowerCase() || '';
        
        if (errorMessage?.includes('email not confirmed') || 
            errorMessage?.includes('email_not_confirmed') ||
            errorMessage?.includes('confirm your email') ||
            errorMessage?.includes('verify your email')) {
          // Email verification required
          setGeneralError('Please verify your email address. Check your inbox and spam folder for the verification link.');
          setShowResendVerification(true);
          logger?.warn('Sign in failed - email not verified', { requestId, email });
        } else if (errorMessage?.includes('invalid login credentials') ||
                   errorMessage?.includes('invalid email or password')) {
          // Invalid credentials - provide helpful message
          setGeneralError('Invalid email or password. If you just signed up, please verify your email first.');
          logger?.warn('Sign in failed - invalid credentials', { requestId, email });
        } else if (errorMessage?.includes('too many requests') ||
                   errorMessage?.includes('rate limit')) {
          // Rate limiting
          setGeneralError('Too many sign-in attempts. Please wait a few minutes and try again.');
          logger?.warn('Sign in failed - rate limited', { requestId, email });
        } else if (errorMessage?.includes('network') ||
                   errorMessage?.includes('fetch')) {
          // Network error
          setGeneralError('Network error. Please check your internet connection and try again.');
          logger?.error('Sign in failed - network error', error, { requestId, email });
        } else {
          // Generic error with helpful context
          setGeneralError('Unable to sign in. Please check your credentials or contact support if the problem persists.');
          logger?.warn('Sign in failed', { requestId, email, error: error?.message });
        }
      } else if (data?.user) {
        // Successful sign-in - navigate to dashboard (not landing page)
        const from = location?.state?.from?.pathname || '/dashboard';
        logger?.info('Sign in successful', { requestId, userId: data?.user?.id });
        trackSignIn('email');
        navigate(from, { replace: true });
      }
    } catch (error) {
      setGeneralError('An unexpected error occurred. Please try again or contact support.');
      logger?.error('Sign in error', error, { requestId });
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendCooldown > 0 || !email) {
      return;
    }

    setResendLoading(true);
    setResendSuccess(false);
    setGeneralError('');

    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { error } = await resendVerificationEmail(email);

      if (error) {
        setGeneralError(`Failed to resend verification email: ${error?.message || 'Please try again or contact support.'}`);
        logger?.error('Resend verification failed', error, { requestId, email });
      } else {
        setResendSuccess(true);
        setResendCooldown(60); // 60 second cooldown
        setSuccessMessage('Verification email sent! Please check your inbox and spam folder.');
        logger?.info('Verification email resent', { requestId, email });
      }
    } catch (error) {
      setGeneralError('An unexpected error occurred. Please try again or contact support.');
      logger?.error('Resend verification error', error, { requestId });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>MyBodyToday - Your Trusted Health Companion</title>
        <meta name="description" content="Your trusted companion for daily symptom tracking and medical reporting" />
      </Helmet>
      <div className="min-h-screen bg-background px-4 py-8">
        <div className="w-full max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="mb-6">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 mb-6">
              <img 
                src="/assets/images/MyBodyToday_logo_design2-removebg-preview1-1770673381636.png" 
                alt="MyBodyToday Logo" 
                className="w-40 h-40 md:w-56 md:h-56 lg:w-64 lg:h-64 object-contain flex-shrink-0" 
              />
              <div className="flex flex-col gap-3 text-center md:text-left">
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
                  Welcome to MyBodyToday&#8482;
                </h1>
                <p className="text-base md:text-lg lg:text-xl text-foreground/70 leading-relaxed">
                  Your trusted companion for daily symptom tracking and medical reporting
                </p>
                <p className="text-sm md:text-base font-medium text-teal-600 italic">
                  Track small changes before they become big ones&#8482;
                </p>
              </div>
            </div>
          </div>

          {/* Medical Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 max-w-3xl mx-auto">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-amber-900 mb-1">
                  Important: This is a Tracking Tool Only
                </h3>
                <p className="text-xs text-amber-800 leading-relaxed">
                  MyBodyToday is designed solely for symptom tracking and record-keeping. This app does not provide medical advice, diagnosis, or treatment recommendations. Always consult with qualified healthcare professionals for medical guidance. In case of emergency, call 911 or your local emergency services immediately.
                </p>
              </div>
            </div>
          </div>

          {/* Sign In Form */}
          <div className="bg-card rounded-lg shadow-therapeutic-lg border border-border p-6 sm:p-8 max-w-md mx-auto">
            {/* New User Prompt - Prominent Banner */}
            <div className="mb-6 p-4 rounded-lg bg-teal-50 border-2 border-teal-300">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <Icon name="UserPlus" size={24} className="text-teal-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-teal-900 mb-1">
                    New to MyBodyToday?
                  </h3>
                  <p className="text-sm text-teal-800 mb-3">
                    If you don't have an account yet, please click "Create New Account" below instead of entering your email here.
                  </p>
                  <Link to={referralCode ? `/sign-up?ref=${referralCode}` : '/sign-up'}>
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                      iconName="ArrowRight"
                    >
                      Create Account Now
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
              Sign In
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Success Message */}
              {successMessage && (
                <div className="flex items-start gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-700">{successMessage}</p>
                </div>
              )}

              {/* General Error Message */}
              {generalError && (
                <div className="flex flex-col gap-3">
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                    <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive">{generalError}</p>
                  </div>

                  {/* Resend Verification Email Section */}
                  {showResendVerification && (
                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                      <div className="flex items-start gap-3 mb-3">
                        <Icon name="Mail" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-semibold text-blue-900 mb-1">
                            Didn't receive the verification email?
                          </h4>
                          <p className="text-xs text-blue-800">
                            Check your spam folder or click below to resend the verification link.
                          </p>
                        </div>
                      </div>

                      {resendSuccess && (
                        <div className="flex items-start gap-2 mb-3 p-3 rounded bg-green-50 border border-green-200">
                          <Icon name="CheckCircle" size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-green-700">
                            Verification email sent successfully! Please check your inbox.
                          </p>
                        </div>
                      )}

                      <Button
                        type="button"
                        onClick={handleResendVerification}
                        disabled={resendLoading || resendCooldown > 0}
                        variant="outline"
                        className="w-full"
                        size="sm"
                      >
                        {resendLoading ? (
                          <>
                            <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : resendCooldown > 0 ? (
                          <>
                            <Icon name="Clock" size={16} className="mr-2" />
                            Resend in {resendCooldown}s
                          </>
                        ) : (
                          <>
                            <Icon name="Mail" size={16} className="mr-2" />
                            Resend Verification Email
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Email Input */}
              <Input
                type="email"
                name="email"
                label="Email Address"
                placeholder="your.email@example.com"
                value={email}
                onChange={handleChange}
                error={error?.email}
                required
                disabled={loading}
                autoComplete="email"
                inputMode="email"
                tabIndex={1}
                maxLength={255}
              />

              {/* Password Input */}
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  label="Password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={handleChange}
                  error={error?.password}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  tabIndex={2}
                  maxLength={128}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={3}
                  disabled={loading}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                </button>
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors min-h-[44px] inline-flex items-center"
                  tabIndex={4}
                >
                  Forgot password?
                </Link>
              </div>

              {/* Sign In Button */}
              <Button
                type="submit"
                fullWidth
                loading={loading}
                disabled={loading}
                size="lg"
                tabIndex={5}
                className="min-h-[44px] text-base"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            {/* Secondary Actions */}
            <div className="space-y-3">
              <Link to={referralCode ? `/sign-up?ref=${referralCode}` : '/sign-up'}>
                <Button
                  type="button"
                  variant="default"
                  fullWidth
                  size="lg"
                  tabIndex={6}
                  className="bg-teal-600 hover:bg-teal-700 text-white font-bold text-lg min-h-[44px] shadow-lg hover:shadow-xl transition-all"
                  iconName="UserPlus"
                >
                  Create New Account
                </Button>
              </Link>
            </div>
          </div>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              © Viewgraph Solutions Ltd
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;